import { useMemo, useState, useRef, useEffect } from 'react';
import { wchIspReadUID, wchIspFlash } from '../lib/wch';
import { tryApiRequest, API_BASE, isLoggedIn } from '../lib/api';

type Stage =
  | 'idle'
  | 'checking'
  | 'shop'
  | 'non-shop'
  | 'no-response'
  | 'error';

const supportInfo = {
  hotline: '0900 000 000',
  zalo: 'https://zalo.me/0900000000',
  email: 'support@3dsmart.local',
};

export default function AuthenticityCheckPage() {
  const [stage, setStage] = useState<Stage>('idle');
  const [deviceId, setDeviceId] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [flashLog, setFlashLog] = useState<string[]>([]);
  const logEndRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (flashLog.length > 0) {
      logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [flashLog]);

  const addLog = (msg: string) => {
    setFlashLog(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);
  };

  const statusText = useMemo(() => {
    switch (stage) {
      case 'idle':
        return 'San sang kiem tra BMCU.';
      case 'checking':
        return 'Dang gui lenh check ID toi BMCU...';
      case 'shop':
        return 'BMCU chinh hang cua shop. Thiet bi hop le de nap firmware.';
      case 'non-shop':
        return 'Khong phai BMCU cua shop. Vui long lien he de duoc ho tro kich hoat.';
      case 'no-response':
        return 'Khong nhan duoc phan hoi tu BMCU. Kiem tra cap ket noi va cong COM.';
      case 'error':
        return 'Da co loi trong qua trinh thao tac.';
      default:
        return '';
    }
  }, [stage]);

  const isBusy = stage === 'checking';

  async function performCheck(port: any) {
    try {
      const uid = await wchIspReadUID(port, addLog, false);
      if (!uid) {
        setStage('no-response');
        return;
      }

      setDeviceId(uid);
      
      const res = await tryApiRequest<{ status: string }>('/device/check-uid', {
        method: 'POST',
        body: JSON.stringify({ deviceUid: uid })
      }, true);

      if (!res) {
        setStage('error');
        setErrorMessage('Lỗi kết nối máy chủ. Vui lòng thử lại sau.');
        return;
      }

      // not_found -> non-shop
      // unactivated, owned, owned_by_other -> shop
      if (res.status === 'not_found') {
        setStage('non-shop');
      } else {
        setStage('shop');
      }

    } catch (error) {
      setStage('error');
      setErrorMessage(error instanceof Error ? error.message : 'Lỗi không xác định');
    }
  }

  async function onCheck() {
    if (!isLoggedIn()) {
      setErrorMessage('Vui lòng đăng nhập để sử dụng tính năng này.');
      setStage('error');
      return;
    }

    if (!('serial' in navigator)) {
      setErrorMessage('Trình duyệt không hỗ trợ Web Serial API. Vui lòng dùng Chrome, Edge hoặc Cốc Cốc.');
      setStage('error');
      return;
    }

    setErrorMessage('');
    setStage('checking');
    setFlashLog([]);

    try {
      const port = await (navigator as any).serial.requestPort();
      await performCheck(port);
      await port.close();
    } catch (error) {
      console.error(error);
      setStage('error');
      setErrorMessage('Không thể mở cổng COM. Đảm bảo bạn đã chọn cổng và không có ứng dụng nào khác đang chiếm cổng.');
    }
  }



  return (
    <div className="page-block">
      <div className="section-heading">
        <span className="eyebrow">Authenticity Check</span>
        <h1>Kiem tra BMCU chinh hang</h1>
      </div>

      <div className="checker-grid">
        <section className="checker-card">
          <h2>1) Kiem tra phan hoi thiet bi</h2>
          <p>
            Hệ thống sẽ gửi lệnh đọc ID qua cổng USB để xác minh thiết bị chính hãng.
          </p>

          <button type="button" className="button primary" onClick={onCheck} disabled={isBusy}>
            Gửi lệnh check ID
          </button>
        </section>

        <section className="checker-card checker-status">
          <h2>2) Trang thai</h2>
          <p className="checker-message">{statusText}</p>

          {deviceId ? (
            <div className="id-box">
              <span>ID BMCU</span>
              <strong>{deviceId}</strong>
            </div>
          ) : null}



          {stage === 'no-response' ? (
            <div className="hint-box warning">
              <h3>Neu van khong phan hoi</h3>
              <ul>
                <li>Kiem tra cap USB va nguon cap cho BMCU</li>
                <li>Kiem tra cong COM da duoc nhan hay chua</li>
                <li>Thu rut cap, cam lai va check lai</li>
              </ul>
            </div>
          ) : null}

          {stage === 'non-shop' ? (
            <div className="hint-box">
              <h3>Lien he ho tro kich hoat</h3>
              <p>Gui ID BMCU cho shop de duoc them vao he thong external enrollment.</p>
              <ul>
                <li>Hotline: {supportInfo.hotline}</li>
                <li>
                  Zalo: <a href={supportInfo.zalo} target="_blank" rel="noreferrer">{supportInfo.zalo}</a>
                </li>
                <li>Email: {supportInfo.email}</li>
              </ul>
            </div>
          ) : null}

          {stage === 'shop' ? (
            <div className="hint-box success">
              <h3>BMCU hop le</h3>
              <p>Thiet bi da du dieu kien de tiep tuc nap firmware chinh thuc.</p>
            </div>
          ) : null}

          {stage === 'error' ? (
            <div className="hint-box warning">
              <h3>Da xay ra loi</h3>
              <p>{errorMessage || 'Vui long thu lai sau.'}</p>
            </div>
          ) : null}
          {stage === 'checking' ? (
            <div style={{ marginTop: '20px', background: '#0f172a', padding: '12px', borderRadius: '8px', color: '#10b981', fontFamily: 'monospace', fontSize: '13px', maxHeight: '150px', overflowY: 'auto' }}>
              {flashLog.map((log, i) => <div key={i}>{log}</div>)}
              <div ref={logEndRef} />
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
